import { PrinterOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import dayjs from "dayjs";
import { useRef, useState, useEffect } from "react";
import Barcode from "react-barcode";
import { useAuth } from "../../contexts/AuthContext";
import { getEmpresa } from "../../services/empresaService";
import "./CierreDeCajaStyles.css";
import { downloadImage, convertHtmlToCanvas } from '../../utils/utils';
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { useConfig } from "../../contexts/ConfigContext";

const CierreTicket = ({ open, onClose, cierre, detalles = [], caja, tpv }) => {
	const componentRef = useRef(null);
	const { currentUser } = useAuth();
	const [empresaData, setEmpresaData] = useState(null);
	const [tpvData, setTPVData] = useState(null);
	const { country } = useConfig();

	// Utilidades para evitar errores de coma flotante: trabajar en centavos
	const toCents = (value) => {
		const safe = value ?? 0;
		return Math.round((safe + Number.EPSILON) * 100);
	};

	const fromCents = (cents) => cents / 100;

	// Formatear la fecha y hora actual
	const now = dayjs();
	const formattedDate = now.format("DD/MM/YYYY");
	const formattedTime = now.format("HH:mm:ss");

	// Cargar datos de la empresa
	useEffect(() => {
		const loadEmpresaData = async () => {
			try {
				const empresa = await getEmpresa();
				setEmpresaData(empresa);
			} catch (error) {
				console.error("Error al cargar datos de la empresa:", error);
				// Continuar sin datos de empresa
			}
		};

		if (open) {
			loadEmpresaData();
		}
	}, [open]);

	// Cargar datos del TPV
	useEffect(() => {
		const loadTPVData = async () => {
			try {
				// Si ya viene el TPV como string (número), cargarlo
				if (tpv) {
					const { getTPVByNoOrId } = await import("../../services/tpvService");
					const tpvInfo = await getTPVByNoOrId(tpv);
					setTPVData(tpvInfo);
				}
			} catch (error) {
				console.error("Error al cargar datos del TPV:", error);
				// Continuar sin datos de TPV
			}
		};

		if (open && tpv) {
			loadTPVData();
		}
	}, [open, tpv]);

	// Función para obtener el nombre completo del país
	const getCountryName = (countryCode) => {
		if (countryCode === "EC") return "ECUADOR";
		return countryCode; // Para otros países, devolver el código tal como viene
	};

	// Calcular totales trabajando en centavos para evitar redondeos intermedios
	const calculateTotals = () => {
		const totalDetallesCents = detalles.reduce(
			(sum, detalle) => sum + toCents(detalle.MontoPago || 0),
			0
		);
		const importeCierreCents = toCents(cierre?.ImporteDeCierreUS || 0);
		const arqueoEfectivoCents = toCents(cierre?.ArqueoEfectivoUS || 0);
		const arqueoTarjetaCents = toCents(cierre?.ArqueoTarjetaUS || 0);
		const arqueoTotalCents = arqueoEfectivoCents + arqueoTarjetaCents;
		const diferenciaCents = arqueoTotalCents - importeCierreCents;

		return {
			totalDetalles: fromCents(totalDetallesCents),
			importeCierre: fromCents(importeCierreCents),
			arqueoEfectivo: fromCents(arqueoEfectivoCents),
			arqueoTarjeta: fromCents(arqueoTarjetaCents),
			arqueoTotal: fromCents(arqueoTotalCents),
			diferencia: fromCents(diferenciaCents),
		};
	};

	// Calcular totales por tipo de pago separando facturas y notas de crédito sin redondeos intermedios
	const calculateTotalsByType = () => {
		// Facturas (valores positivos)
		const facturasEfectivoCents = detalles
			.filter(
				(detalle) =>
					(detalle.TipoPago || "Efectivo") === "Efectivo" &&
					(detalle.MontoPago || 0) > 0
			)
			.reduce((sum, detalle) => sum + toCents(detalle.MontoPago || 0), 0);

		const facturasTarjetaCents = detalles
			.filter(
				(detalle) =>
					(detalle.TipoPago || "Efectivo") === "Tarjeta" &&
					(detalle.MontoPago || 0) > 0
			)
			.reduce((sum, detalle) => sum + toCents(detalle.MontoPago || 0), 0);

		// Notas de crédito (valores negativos)
		const notasCreditoEfectivoCents = detalles
			.filter(
				(detalle) =>
					(detalle.TipoPago || "Efectivo") === "Efectivo" &&
					(detalle.MontoPago || 0) < 0
			)
			.reduce((sum, detalle) => sum + Math.abs(toCents(detalle.MontoPago || 0)), 0);

		const notasCreditoTarjetaCents = detalles
			.filter(
				(detalle) =>
					(detalle.TipoPago || "Efectivo") === "Tarjeta" &&
					(detalle.MontoPago || 0) < 0
			)
			.reduce((sum, detalle) => sum + Math.abs(toCents(detalle.MontoPago || 0)), 0);

		// Totales generales en centavos
		const totalEfectivoCents = facturasEfectivoCents - notasCreditoEfectivoCents;
		const totalTarjetaCents = facturasTarjetaCents - notasCreditoTarjetaCents;

		return {
			facturasEfectivo: fromCents(facturasEfectivoCents),
			facturasTarjeta: fromCents(facturasTarjetaCents),
			notasCreditoEfectivo: fromCents(notasCreditoEfectivoCents),
			notasCreditoTarjeta: fromCents(notasCreditoTarjetaCents),
			totalEfectivo: fromCents(totalEfectivoCents),
			totalTarjeta: fromCents(totalTarjetaCents),
			total: fromCents(totalEfectivoCents + totalTarjetaCents),
		};
	};

	const { totalDetalles, importeCierre, arqueoEfectivo, arqueoTarjeta, arqueoTotal, diferencia } =
		calculateTotals();
		
	const {
		facturasEfectivo,
		facturasTarjeta,
		notasCreditoEfectivo,
		notasCreditoTarjeta,
		totalEfectivo,
		totalTarjeta,
	} = calculateTotalsByType();

	// Función para imprimir directamente la imagen
	const printImage = async () => {
		try {
			const canvas = await convertHtmlToCanvas(componentRef.current);
			if (!canvas) return;

			// Obtener imagen como data URL
			const image = canvas.toDataURL("image/png");

			// Crear ventana para impresión
			const printWindow = window.open("", "_blank");

			if (!printWindow) {
				window.messageApi.warning(
					"El navegador bloqueó la ventana emergente. Por favor, permite ventanas emergentes para imprimir."
				);
				return;
			}

			// Escribir HTML con la imagen
			printWindow.document.write(`
                <html>
                <head>
                    <title>Comprobante de cierre ${cierre?.No || cierre?.id || ""
				}</title>
                    <style>
                        body {
                            margin: 0;
                            padding: 0;
                            display: flex;
                            justify-content: center;
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                        }
                        @media print {
                            body {
                                width: 80mm; /* Ancho estándar para tickets */
                                margin: 0 auto;
                            }
                        }
                    </style>
                </head>
                <body>
                    <img src="${image}" alt="Ticket" />
                    <script>
                        // Imprimir automáticamente
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 200);
                        };
                    </script>
                </body>
                </html>
            `);

			printWindow.document.close();

			// Notificar que se ha impreso y cerrar el modal automáticamente
			setTimeout(() => {
				window.messageApi.success("Comprobante enviado a impresión");
				onClose(); // Esto continuará con el proceso de cierre
			}, 1000);
		} catch (error) {
			console.error("Error al imprimir:", error);
			window.messageApi.error("Error al imprimir el ticket");
		}
	};

	// Función para descargar la imagen del ticket
	const downloadTicketImage = async () => {
		await downloadImage({
			element: componentRef.current,
			fileName:`cierre-${cierre?.No || cierre?.id || formattedDate.replace(/\//g, "-")}`,
			onSuccess: (msg) => window.messageApi.success(msg),
			onError: (msg) => window.messageApi.error(msg),
		});
	};

	return (
		<Modal
			title="Comprobante de Cierre de Caja - Paso Final"
			open={open}
			onCancel={onClose}
			footer={[
				<Button key="download" onClick={downloadTicketImage}>
					Descargar Imagen
				</Button>,
				<Button key="close" onClick={onClose}>
					Continuar sin Imprimir
				</Button>,
				<Button
					key="print"
					type="primary"
					icon={<PrinterOutlined />}
					onClick={printImage}
				>
					Imprimir y Continuar
				</Button>,
			]}
			maskClosable={false}
			closable={false}
			width={400}
			centered
			className="ticket-preview-modal"
		>
			<div className="ticket-container">
				<div className="ticket-content" ref={componentRef}>
					{/* Cabecera del ticket */}
					<div className="ticket-header">
						<div style={{ textAlign: "center", marginBottom: "20px" }}>
							<div
								style={{
									fontWeight: "bold",
									fontSize: "16px",
									marginBottom: "8px",
								}}
							>
								{empresaData?.Name || "Empresa"}
							</div>
							<div style={{ fontSize: "14px", marginBottom: "8px" }}>
								{empresaData?.VAT_Registration_No || "Sin RUC"}
							</div>
							{tpvData?.nombre && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{tpvData.nombre}
								</div>
							)}
							{tpvData?.pais && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{getCountryName(tpvData.pais)}
								</div>
							)}
							{tpvData?.nifCif && (
								<div style={{ fontSize: "14px", marginBottom: "8px" }}>
									{tpvData.nifCif}
								</div>
							)}
						</div>

						<div style={{ textAlign: "center", margin: "15px 0" }}>
							<div
								style={{
									border: "1px solid #000",
									display: "inline-block",
									padding: "8px 16px",
								}}
							>
								<strong style={{ fontSize: "16px" }}>
									COMPROBANTE DE CIERRE
								</strong>
							</div>
						</div>

						<div style={{ margin: "20px 0" }}>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>CIERRE Nº:</span>
								<span>
									{cierre?.No
										? String(cierre.No).padStart(6, "0")
										: cierre?.id
											? String(cierre.id).padStart(6, "0")
											: "000001"}
								</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>CAJA:</span>
								<span>{caja || cierre?.Caja || "1"}</span>
								<span style={{ fontWeight: "bold", marginLeft: "20px" }}>
									TPV:
								</span>
								<span>{tpv || cierre?.TPV || "1"}</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>CAJERO:</span>
								<span>
									{cierre?.Cajero || currentUser?.username || "Usuario Sistema"}
								</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>FECHA APERTURA:</span>
								<span>
									{cierre?.FechaDeApertura
										? dayjs(cierre.FechaDeApertura).format("DD/MM/YYYY")
										: formattedDate}
								</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>FECHA CIERRE:</span>
								<span>
									{cierre?.FechaDeCierre
										? dayjs(cierre.FechaDeCierre).format("DD/MM/YYYY")
										: formattedDate}
								</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>HORA IMPRESIÓN:</span>
								<span>{formattedTime}</span>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "3px",
									fontSize: "10px",
								}}
							>
								<span style={{ fontWeight: "bold" }}>ESTADO:</span>
								<span
									style={{
										fontWeight: "bold",
										color: cierre?.Estado === "Cerrado" ? "#f50" : "#52c41a",
									}}
								>
									{cierre?.Estado || "Cerrado"}
								</span>
							</div>
						</div>
					</div>

					<div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>

					{/* Información de montos */}
					<div style={{ marginBottom: "20px" }}>
						<h4
							style={{
								margin: "0 0 12px 0",
								fontSize: "14px",
								fontWeight: "bold",
							}}
						>
							RESUMEN DE CAJA
						</h4>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
								fontSize: "12px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>IMPORTE APERTURA:</span>
							<span>{formatNumberByCountry(cierre?.ImporteDeApertura || 0, country, true)}</span>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
								fontSize: "12px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>IMPORTE CIERRE:</span>
							<span>{formatNumberByCountry(importeCierre, country, true)}</span>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
								fontSize: "12px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>ARQUEO EFECTIVO:</span>
							<span>{formatNumberByCountry(arqueoEfectivo, country, true)}</span>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
								fontSize: "12px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>ARQUEO TARJETA:</span>
							<span>{formatNumberByCountry(arqueoTarjeta, country, true)}</span>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								marginBottom: "6px",
								fontSize: "12px",
								borderTop: "1px solid #ccc",
								paddingTop: "4px",
								marginTop: "4px",
							}}
						>
							<span style={{ fontWeight: "bold" }}>ARQUEO TOTAL:</span>
							<span>{formatNumberByCountry(arqueoTotal, country, true)}</span>
						</div>

						<div
							style={{
								borderTop: "1px solid #000",
								paddingTop: "8px",
								marginTop: "8px",
							}}
						>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<span style={{ fontWeight: "bold", fontSize: "14px" }}>
									DIFERENCIA:
								</span>
								<span
									style={{
										fontWeight: "bold",
										fontSize: "14px",
										color: Math.abs(diferencia) > 0.01 ? "#f50" : "#52c41a",
									}}
								>
									{formatNumberByCountry(diferencia, country, true)}
								</span>
							</div>
						</div>
					</div>

					{/* Desglose por tipo de pago separando facturas y notas de crédito */}
					{detalles && detalles.length > 0 && (
						<>
							<div
								style={{ borderTop: "1px dashed #000", margin: "15px 0" }}
							></div>
							<div style={{ marginBottom: "20px" }}>
								<h4
									style={{
										margin: "0 0 12px 0",
										fontSize: "14px",
										fontWeight: "bold",
									}}
								>
									DESGLOSE POR TIPO DE PAGO
								</h4>

								{/* Facturas */}
								<div style={{ marginBottom: "12px" }}>
									<div
										style={{
											fontSize: "12px",
											fontWeight: "bold",
											marginBottom: "6px",
											textDecoration: "underline",
										}}
									>
										FACTURAS:
									</div>

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
											fontSize: "11px",
											paddingLeft: "10px",
										}}
									>
										<span>Efectivo:</span>
										<span>{formatNumberByCountry(diferencia, country, true)}</span>
									</div>

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
											fontSize: "11px",
											paddingLeft: "10px",
										}}
									>
										<span>Tarjeta:</span>
										<span>{formatNumberByCountry(facturasTarjeta, country, true)}</span>
									</div>

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											fontSize: "11px",
											paddingLeft: "10px",
											fontWeight: "bold",
											borderTop: "1px solid #ccc",
											paddingTop: "2px",
										}}
									>
										<span>Subtotal Facturas:</span>
										<span>
											{formatNumberByCountry((facturasEfectivo + facturasTarjeta), country, true)}
										</span>
									</div>
								</div>

								{/* Notas de Crédito */}
								{(notasCreditoEfectivo > 0 || notasCreditoTarjeta > 0) && (
									<div style={{ marginBottom: "12px" }}>
										<div
											style={{
												fontSize: "12px",
												fontWeight: "bold",
												marginBottom: "6px",
												textDecoration: "underline",
											}}
										>
											NOTAS DE CRÉDITO:
										</div>

										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												marginBottom: "4px",
												fontSize: "11px",
												paddingLeft: "10px",
											}}
										>
											<span>Efectivo:</span>
											<span>-{formatNumberByCountry(notasCreditoEfectivo, country, true)}</span>
										</div>

										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												marginBottom: "4px",
												fontSize: "11px",
												paddingLeft: "10px",
											}}
										>
											<span>Tarjeta:</span>
											<span>-{formatNumberByCountry(notasCreditoTarjeta, country, true)}</span>
										</div>

										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												fontSize: "11px",
												paddingLeft: "10px",
												fontWeight: "bold",
												borderTop: "1px solid #ccc",
												paddingTop: "2px",
											}}
										>
											<span>Subtotal Notas Crédito:</span>
											<span>
												-$
												{formatNumberByCountry((notasCreditoEfectivo + notasCreditoTarjeta), country, true)}
											</span>
										</div>
									</div>
								)}

								{/* Totales finales */}
								<div
									style={{
										borderTop: "1px solid #000",
										paddingTop: "8px",
										marginTop: "8px",
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
											fontSize: "12px",
											fontWeight: "bold",
										}}
									>
										<span>TOTAL EFECTIVO:</span>
										<span>{formatNumberByCountry(totalEfectivo, country, true)}</span>
									</div>

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											marginBottom: "4px",
											fontSize: "12px",
											fontWeight: "bold",
										}}
									>
										<span>TOTAL TARJETA:</span>
										<span>{formatNumberByCountry(totalTarjeta, country, true)}</span>
									</div>

									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											fontSize: "12px",
											fontWeight: "bold",
											borderTop: "1px solid #000",
											paddingTop: "4px",
											marginTop: "4px",
										}}
									>
										<span>TOTAL GENERAL:</span>
										<span>{formatNumberByCountry((totalEfectivo + totalTarjeta), country, true)}</span>
									</div>
								</div>
							</div>
						</>
					)}

					{/* Detalles por forma de pago */}
					{detalles && detalles.length > 0 && (
						<>
							<div
								style={{ borderTop: "1px dashed #000", margin: "15px 0" }}
							></div>
							<div style={{ marginBottom: "20px" }}>
								<h4
									style={{
										margin: "0 0 12px 0",
										fontSize: "14px",
										fontWeight: "bold",
									}}
								>
									DETALLES POR FORMA DE PAGO
								</h4>

								<div
									style={{
										display: "grid",
										gridTemplateColumns: "2fr 1fr",
										gap: "8px",
										fontWeight: "bold",
										marginBottom: "8px",
										borderBottom: "1px solid #000",
										paddingBottom: "4px",
										fontSize: "12px",
									}}
								>
									<span>FORMA DE PAGO</span>
									<span style={{ textAlign: "right" }}>MONTO</span>
								</div>

								{detalles.map((detalle, index) => (
									<div
										key={index}
										style={{
											display: "grid",
											gridTemplateColumns: "2fr 1fr",
											gap: "8px",
											marginBottom: "6px",
											fontSize: "11px",
										}}
									>
										<span>{detalle.DesFormaPago || detalle.idFormaPago}</span>
										<span style={{ textAlign: "right", fontWeight: "bold" }}>
											{formatNumberByCountry((detalle?.MontoPago || 0), country, true)}
										</span>
									</div>
								))}

								<div
									style={{
										borderTop: "1px solid #000",
										paddingTop: "8px",
										marginTop: "8px",
									}}
								>
									<div
										style={{
											display: "grid",
											gridTemplateColumns: "2fr 1fr",
											gap: "8px",
										}}
									>
										<span style={{ fontWeight: "bold", fontSize: "12px" }}>
											TOTAL:
										</span>
										<span
											style={{
												fontWeight: "bold",
												fontSize: "12px",
												textAlign: "right",
											}}
										>
											{formatNumberByCountry((totalDetalles), country, true)}
										</span>
									</div>
								</div>
							</div>
						</>
					)}

					{/* Información adicional */}
					{cierre?.Comprobante_Tarjetas && (
						<>
							<div
								style={{ borderTop: "1px dashed #000", margin: "15px 0" }}
							></div>
							<div style={{ marginBottom: "20px" }}>
								<h4
									style={{
										margin: "0 0 8px 0",
										fontSize: "14px",
										fontWeight: "bold",
									}}
								>
									INFORMACIÓN ADICIONAL
								</h4>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										marginBottom: "4px",
										fontSize: "12px",
									}}
								>
									<span>Total Tarjetas:</span>
									<span style={{ fontWeight: "bold" }}>
										{cierre.Comprobante_Tarjetas}
									</span>
								</div>
							</div>
						</>
					)}

					<div style={{ borderTop: "1px dashed #000", margin: "20px 0" }}></div>

					<div style={{ textAlign: "center", marginBottom: "20px" }}>
						<div style={{ fontSize: "16px", fontWeight: "bold" }}>
							CIERRE DE CAJA COMPLETADO
						</div>
					</div>

					<div
						style={{
							textAlign: "center",
							marginBottom: "15px",
							padding: "8px",
							backgroundColor: "#fff2e8",
							border: "1px solid #ffa940",
							borderRadius: "4px",
						}}
					>
						<div
							style={{ fontSize: "12px", fontWeight: "bold", color: "#ad6800" }}
						>
							DOCUMENTO INTERNO DE CONTROL
						</div>
					</div>

					<div style={{ textAlign: "center", marginTop: "20px" }}>
						<Barcode
							value={
								cierre?.No
									? String(cierre.No).padStart(6, "0")
									: cierre?.id
										? String(cierre.id).padStart(6, "0")
										: "000001"
							}
							width={1.5}
							height={40}
							fontSize={12}
							marginTop={10}
							displayValue={true}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default CierreTicket;
